<script setup lang="ts">
  import HeaderWithActions from '@/components/projectDetail/components/HeaderWithActions.vue'
  import PrimaryButton from '@/components/shared/PrimaryButton.vue'
  import ClassificationUploadDialog from '@/components/projectDetail/ClassificationDrawer/ClassificationUploadDialog.vue'
  import { GEOLOGY_CODE, GEOLOGY_CODE_2 } from '@/stores/giDataUploadCollection'
  import ColorPickerCell from '@/components/projectDetail/components/ColorPickerCell.vue'
  import { GENERAL_DEBOUND_TIME } from '@/constants/constants'
  import ComboBox from '@/components/projectDetail/components/ComboBox.vue'

  const store = useProjectStore()
  const classificationDrawer = ref<HTMLElement | null>(null)
  const authorizationStore = useAuthorizationStore()
  const giDataUploadCollectionStore = useGIDataUploadCollectionStore()

  const canAdd = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: 'add_gidatauploadcollection'
    })
  })

  useResizeableElement({
    ref: classificationDrawer,
    widthProperty: 'classificationDrawer',
    elementGetter: vDrawerElementGetter
  })

  onMounted(() => {
    const gi_data_upload_collection_id = giDataUploadCollectionStore.items[0]?.id
    if (!gi_data_upload_collection_id) {
      return
    }
    giDataUploadCollectionStore.selectedGICollectionForUploadClassification =
      gi_data_upload_collection_id
    giDataUploadCollectionStore.fetchClassificationValueOverview(gi_data_upload_collection_id)
  })

  watch(
    () => giDataUploadCollectionStore.selectedGICollectionForUploadClassification,
    (newValue, oldValue) => {
      if (!newValue || newValue === oldValue) {
        return
      }
      giDataUploadCollectionStore.fetchClassificationValueOverview(newValue)
    }
  )

  const onColorChanged = useDebounceFn(async (colorUpdate: any) => {
    await giDataUploadCollectionStore.updateClassificationColor(
      colorUpdate.color,
      colorUpdate.primaryClassName
    )
  }, GENERAL_DEBOUND_TIME)
</script>

<template>
  <v-navigation-drawer
    :temporary="true"
    :scrim="false"
    ref="classificationDrawer"
    :model-value="store.classificationDrawer.shown"
    :width="store.classificationDrawer.width"
    :class="['classification-drawer', 'resize-handler-elevation']"
  >
    <HeaderWithActions>
      <template #default> Classification Management</template>
      <template #actions>
        <v-tooltip text="Upload classifications" v-if="canAdd">
          <template v-slot:activator="{ props }">
            <PrimaryButton
              prepend-icon="mdi-plus"
              v-bind="props"
              @click="giDataUploadCollectionStore.toggleClassificationUploadDialog()"
            >
              Add
            </PrimaryButton>
          </template>
        </v-tooltip>
      </template>
      <template #subheader> Upload and manage classification data </template>
    </HeaderWithActions>
    <v-divider />
    <div>
      <v-row no-gutters class="mx-2 mt-2">
        <v-col cols="4">
          <ComboBox
            v-model="giDataUploadCollectionStore.selectedGICollectionForUploadClassification"
            :items="giDataUploadCollectionStore.items"
            :disabled="giDataUploadCollectionStore.loadingUploadClassification"
            item-value="id"
            item-title="name"
            label="GI data upload collection"
          ></ComboBox
        ></v-col>
        <v-col cols="4">
          <ComboBox
            v-model="giDataUploadCollectionStore.selectedClasssificationColumnName"
            :items="[GEOLOGY_CODE, GEOLOGY_CODE_2]"
            label="Select classification column"
            class="ml-2"
          ></ComboBox
        ></v-col>
      </v-row>
      <v-data-table
        :headers="[
          { title: 'Primary Class Name', key: 'primary_class_name' },
          { title: 'Color', key: 'color_code' }
        ]"
        :items="giDataUploadCollectionStore.displayClassificationOverview"
        class="mt-4"
        density="compact"
        no-data-text="No classification data available."
        hide-default-footer
      >
        <template v-slot:[`item.color_code`]="{ item }">
          <div class="d-flex align-center justify-start">
            <ColorPickerCell
              :model-value="item.color_code"
              @update:modelValue="
                ($event) =>
                  onColorChanged({ color: $event, primaryClassName: item.primary_class_name })
              "
            >
            </ColorPickerCell>
          </div>
        </template>
      </v-data-table>
    </div>
    <ClassificationUploadDialog />
  </v-navigation-drawer>
</template>

<style scoped>
  .resize-handler-elevation {
    border-right-width: 10px;
    z-index: 1;
    box-shadow: none;
  }

  .hidden {
    display: none;
    opacity: 0;
    transition: opacity 0.5s ease;
  }

  :deep(.v-navigation-drawer__content) {
    overflow-y: hidden;
  }

  .classification-drawer {
    transition: border-bottom-color 0.3s;
    border-right: 10px solid rgba(0, 0, 0, 0.1);
  }
</style>
